import React, { useEffect } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectMonthlyStatement,
  selectMonthlyStatementStatus,
} from "../../features/billing/billing-slice";
import { fetchMonthlyStatement } from "../../features/billing/slice-extensions/billing-slice-extension";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { formatMoney } from "../../features/billing/utils/money";
import { exportToExcel } from "../../features/billing/utils/excelExport";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface MonthlyStatementSectionProps {
  year: number;
  month: number;
}

/**
 * The month's retrospective statement, shown under the month-end review:
 * money headlines with last-month deltas, received money by shape, visits
 * delivered by outcome, and the exportable per-member table.
 */
const MonthlyStatementSection: React.FC<MonthlyStatementSectionProps> = ({ year, month }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const statement = useAppSelector(selectMonthlyStatement);
  const status = useAppSelector(selectMonthlyStatementStatus);
  const memberName = useMemberNameLookup();

  useEffect(() => {
    dispatch(fetchMonthlyStatement({ year, month }));
  }, [dispatch, year, month]);

  if (status === LoadingStatus.Loading && !statement) {
    return <div className="mt-10"><TableRowsSkeleton rows={3} /></div>;
  }
  if (!statement || (statement.year !== year || statement.month !== month)) {
    return null;
  }

  const money = (minorUnits: number) => formatMoney(minorUnits, statement.currency, getDisplayLocale());
  const delta = statement.receivedMinorUnits - statement.previousReceivedMinorUnits;
  const chargeable = statement.delivery.attended + statement.delivery.cancelledCharged;
  const unbilled = statement.delivery.attendedComp + statement.delivery.cancelledWaived;

  const headline = [
    { key: "received", label: t("billing.statement.received"), value: money(statement.receivedMinorUnits), accent: "text-green-700 dark:text-green-400" },
    { key: "outstanding", label: t("billing.statement.outstanding"), value: money(statement.outstandingMinorUnits), accent: "text-secondary-40 dark:text-secondary-70" },
    { key: "inDrafts", label: t("billing.statement.inDrafts"), value: money(statement.draftMinorUnits), accent: "text-grey-20 dark:text-white" },
    { key: "voided", label: t("billing.statement.voided"), value: money(statement.voidedMinorUnits), accent: "text-grey-40 dark:text-grey-60" },
  ];

  const handleExport = () => {
    exportToExcel(
      `statement-${statement.year}-${String(statement.month).padStart(2, "0")}`,
      statement.members.map((m) => ({
        [t("billing.table.member")]: memberName(m.memberId),
        [t("billing.table.billed")]: money(m.billedMinorUnits),
        [t("billing.table.received")]: money(m.receivedMinorUnits),
        [t("billing.table.outstanding")]: money(m.outstandingMinorUnits),
      })),
    );
  };

  return (
    <div className="mt-12 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h3>{t("billing.statement.title")}</h3>
        {statement.members.length > 0 && (
          <button type="button" className="btn btn-secondary ml-auto" onClick={handleExport}>
            {t("billing.common.exportToExcel")}
          </button>
        )}
      </div>

      {/* Money headlines */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {headline.map((item) => (
          <div key={item.key} className="with-border rounded-2xl p-4">
            <p className="text-sm text-grey-40 dark:text-grey-80">{item.label}</p>
            <p className={`mt-1 text-2xl font-bold ${item.accent}`}>{item.value}</p>
            {item.key === "received" && statement.previousReceivedMinorUnits > 0 && (
              <p className="mt-1 flex items-center gap-1 text-xs text-grey-40 dark:text-grey-80">
                {delta >= 0
                  ? <TrendingUp size={14} className="text-green-700 dark:text-green-400" />
                  : <TrendingDown size={14} className="text-red-600 dark:text-red-400" />}
                {t("billing.statement.vsPreviousMonth", {
                  delta: `${delta >= 0 ? "+" : "−"}${money(Math.abs(delta))}`,
                })}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Received by shape + delivery */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="with-border rounded-2xl p-4">
          <p className="mb-3 text-sm font-semibold text-grey-30 dark:text-grey-90">{t("billing.statement.receivedByType")}</p>
          {statement.receivedByShape.length === 0 ? (
            <p className="text-sm text-grey-40 dark:text-grey-80">{t("billing.statement.nothingReceived")}</p>
          ) : (
            <ul className="space-y-2">
              {statement.receivedByShape.map((s) => (
                <li key={s.shape} className="flex items-center justify-between text-sm">
                  <span className="text-grey-30 dark:text-grey-80">
                    {t(`billing.statement.shapePlural.${s.shape}`, { defaultValue: s.shape })}
                  </span>
                  <span className="font-bold text-grey-10 dark:text-white">{money(s.receivedMinorUnits)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="with-border rounded-2xl p-4">
          <p className="mb-3 text-sm font-semibold text-grey-30 dark:text-grey-90">{t("billing.statement.visitsDelivered")}</p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between">
              <span className="text-grey-30 dark:text-grey-80">{t("billing.statement.chargeable")}</span>
              <span className="font-bold text-grey-10 dark:text-white">
                {chargeable} ({statement.delivery.attended} · {statement.delivery.cancelledCharged})
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-grey-30 dark:text-grey-80">{t("billing.statement.notBilled")}</span>
              <span className="font-bold text-grey-10 dark:text-white">
                {unbilled} ({statement.delivery.attendedComp} · {statement.delivery.cancelledWaived})
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Per-member table */}
      {statement.members.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-grey-90 dark:border-primary-20">
          <table className="w-full text-sm">
            <thead className="bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90">
              <tr>
                <th className="px-4 py-2 text-left">{t("billing.table.member")}</th>
                <th className="px-4 py-2 text-right">{t("billing.table.billed")}</th>
                <th className="px-4 py-2 text-right">{t("billing.table.received")}</th>
                <th className="px-4 py-2 text-right">{t("billing.table.outstanding")}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
              {statement.members.map((m) => (
                <tr key={m.memberId} className="border-t border-grey-90 dark:border-primary-20">
                  <td className="px-4 py-2">{memberName(m.memberId)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(m.billedMinorUnits)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(m.receivedMinorUnits)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${
                    m.outstandingMinorUnits > 0 ? "font-bold text-secondary-40 dark:text-secondary-70" : ""
                  }`}>
                    {money(m.outstandingMinorUnits)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyStatementSection;
